import { z } from 'zod';
import client from '@/api/client';
import type { LinkCandidate } from '@/api/hooks/useLinkCandidates';
import { decimalAbs, decimalIsNegative, decimalIsZero, decimalSum } from './decimal';

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must use YYYY-MM-DD');
const scaleFourMoneySchema = z.string().regex(/^-?(0|[1-9][0-9]*)\.[0-9]{4}$/, 'money must use scale 4')
  .refine((value) => value !== '-0.0000', 'negative zero is not allowed');
const scaleFourShareSchema = z.string().regex(/^(0\.[0-9]{4}|1\.0000)$/, 'share must be between 0 and 1');
const currencySchema = z.string().regex(/^[A-Z]{3}$/, 'currency must be an ISO 4217 code');
const uuidSchema = z.string().uuid();

const singleMoneySchema = z.object({
  status: z.literal('single'),
  amount: scaleFourMoneySchema,
  currency: currencySchema,
}).strict();

const unsafeMoneySchema = z.object({
  status: z.enum(['mixed', 'unavailable']),
  amount: z.null(),
  currency: z.null(),
}).strict();

const spendMoneySchema = z.discriminatedUnion('status', [singleMoneySchema, unsafeMoneySchema]);

const drilldownSchema = z.object({
  category_id: uuidSchema.nullable().optional(),
  merchant_id: uuidSchema.nullable().optional(),
}).strict().superRefine((drilldown, context) => {
  if (drilldown.category_id !== undefined && drilldown.merchant_id !== undefined
    && drilldown.category_id !== null && drilldown.merchant_id !== null) {
    context.addIssue({ code: 'custom', message: 'drilldown cannot contain both category_id and merchant_id' });
  }
});

const groupSchema = z.object({
  key: z.string().min(1).max(200),
  label: z.string().min(1).max(200),
  total: spendMoneySchema,
  share: scaleFourShareSchema.nullable(),
  line_item_count: z.number().int().nonnegative().nullable(),
  drilldown: drilldownSchema,
}).strict();

const pendingTransferProposalsSchema = z.object({
  count: z.number().int().nonnegative().nullable(),
  status: z.enum(['included', 'unavailable']),
}).strict().superRefine((pending, context) => {
  if (pending.status === 'included' && pending.count === null) {
    context.addIssue({ code: 'custom', message: 'included pending proposals require a count' });
  }
  if (pending.status === 'unavailable' && pending.count !== null) {
    context.addIssue({ code: 'custom', message: 'unavailable pending proposals cannot assert a count' });
  }
});

const aggregationDataSchema = z.object({
  mode: z.enum(['category', 'merchant']),
  scope: z.object({
    date_from: dateSchema.nullable(),
    date_to: dateSchema.nullable(),
    account_ids: z.array(uuidSchema),
  }).strict(),
  groups: z.array(groupSchema),
  reconciliation: z.object({
    total: spendMoneySchema,
    line_item_count: z.number().int().nonnegative().nullable(),
  }).strict(),
  pending_transfer_proposals: pendingTransferProposalsSchema,
}).strict().superRefine((data, context) => {
  data.groups.forEach((group, index) => {
    if (data.mode === 'category' && group.drilldown.merchant_id != null) {
      context.addIssue({ code: 'custom', path: ['groups', index, 'drilldown', 'merchant_id'], message: 'category groups cannot expose merchant drilldown' });
    }
    if (data.mode === 'merchant' && group.drilldown.category_id != null) {
      context.addIssue({ code: 'custom', path: ['groups', index, 'drilldown', 'category_id'], message: 'merchant groups cannot expose category drilldown' });
    }
  });
});

export const spendAggregationSchema = z.object({
  data: aggregationDataSchema,
  meta: z.object({
    coverage: z.object({
      status: z.enum(['ready', 'incomplete', 'failed']),
      safe_reason: z.string().nullable().optional(),
    }).strict(),
    exclusions: z.object({
      confirmed_transfer: z.number().int().nonnegative().nullable(),
      removed: z.number().int().nonnegative().nullable(),
      superseded: z.number().int().nonnegative().nullable(),
      deleted: z.number().int().nonnegative().nullable(),
    }).strict(),
    next_cursor: z.string().min(16).max(4096).nullable(),
  }).strict(),
}).strict();

export type SpendAggregationResponse = z.infer<typeof spendAggregationSchema>;
export type SpendAggregationData = SpendAggregationResponse['data'];
export type SpendAggregationGroup = SpendAggregationData['groups'][number];

export type SpendAggregationQuery = {
  mode: 'category' | 'merchant';
  accountIds: readonly string[];
  dateFrom?: string;
  dateTo?: string;
  cursor?: string | null;
  limit?: number;
};

function normalizedAccountIds(accountIds: readonly string[]): string[] {
  return [...new Set(accountIds.map((accountId) => accountId.toLowerCase()))].sort();
}

export function spendAggregationQueryKey(query: SpendAggregationQuery) {
  return [
    'spend-aggregation',
    {
      mode: query.mode,
      accountIds: normalizedAccountIds(query.accountIds),
      dateFrom: query.dateFrom ?? null,
      dateTo: query.dateTo ?? null,
      cursor: query.cursor ?? null,
      limit: query.limit ?? 50,
    },
  ] as const;
}

export function parseSpendAggregation(payload: unknown): SpendAggregationResponse {
  return spendAggregationSchema.parse(payload);
}

function spendAggregationParams(query: SpendAggregationQuery): URLSearchParams {
  if ((query.dateFrom === undefined) !== (query.dateTo === undefined)) {
    throw new Error('dateFrom and dateTo must be supplied together');
  }

  const params = new URLSearchParams();
  params.set('mode', query.mode);
  if (query.dateFrom !== undefined && query.dateTo !== undefined) {
    params.set('date_from', query.dateFrom);
    params.set('date_to', query.dateTo);
  }
  normalizedAccountIds(query.accountIds).forEach((accountId) => params.append('account_id', accountId));
  if (query.cursor) params.set('cursor', query.cursor);
  params.set('limit', String(query.limit ?? 50));
  return params;
}

export async function fetchSpendAggregation(query: SpendAggregationQuery): Promise<SpendAggregationResponse> {
  const response = await client.get('/api/spend/aggregation', { params: spendAggregationParams(query) });
  return parseSpendAggregation(response.data);
}

/** Presentation-only slug formatting retained for the feed's filter banner. */
export function titleCaseFromSlug(slug: string): string {
  return slug
    .split('.')
    .map((segment) => (segment.length > 0 ? segment[0].toUpperCase() + segment.slice(1) : segment))
    .join(' › ');
}

/**
 * Compatibility disclosure for the existing feed. Category and merchant
 * totals never call this path; they render the server's pending disclosure.
 */
export function pendingTransferSpendTotal(candidates: LinkCandidate[]): string | null {
  const amounts: string[] = [];
  for (const candidate of candidates) {
    for (const leg of candidate.legs) {
      if (leg.superseded_at !== null || leg.has_live_link || !decimalIsNegative(leg.amount)) continue;
      amounts.push(leg.amount);
    }
  }
  if (amounts.length === 0) return null;
  const total = decimalAbs(decimalSum(amounts));
  return decimalIsZero(total) ? null : total;
}
